import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation, useParams, type Params } from 'react-router-dom';
import { useSession } from './lib/session';
import { Shell } from './components/Shell';
import { Loading } from './components/ui';
import Login, { SessionExpired } from './pages/Login';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const StockMove = lazy(() => import('./pages/stock/StockMove'));
const Inventory = lazy(() => import('./pages/catalog/Inventory'));
const Movements = lazy(() => import('./pages/stock/Movements'));
const Counts = lazy(() => import('./pages/stock/Counts'));
const CountDetail = lazy(() => import('./pages/stock/CountDetail'));
const Reorder = lazy(() => import('./pages/purchasing/Reorder'));
const PurchaseOrders = lazy(() => import('./pages/purchasing/PurchaseOrders'));
const PurchaseOrderDetail = lazy(() => import('./pages/purchasing/PurchaseOrderDetail'));
const PurchaseRequests = lazy(() => import('./pages/purchasing/PurchaseRequests'));
const PurchaseRequestForm = lazy(() => import('./pages/purchasing/PurchaseRequestForm'));
const PurchaseRequestDetail = lazy(() => import('./pages/purchasing/PurchaseRequestDetail'));
const ItemImport = lazy(() => import('./pages/setup/ItemImport'));
const ReceiptDetail = lazy(() => import('./pages/purchasing/ReceiptDetail'));
const Reports = lazy(() => import('./pages/insight/Reports'));
const Periods = lazy(() => import('./pages/insight/Periods'));
const Items = lazy(() => import('./pages/catalog/Items'));
const ItemProfile = lazy(() => import('./pages/catalog/ItemProfile'));
const Suppliers = lazy(() => import('./pages/setup/Suppliers'));
const ParLevels = lazy(() => import('./pages/setup/ParLevels'));
const Kits = lazy(() => import('./pages/setup/Kits'));
const PropertySettings = lazy(() => import('./pages/setup/PropertySettings'));
const SupplierProfile = lazy(() => import('./pages/setup/SupplierProfile'));
const Locations = lazy(() => import('./pages/setup/Locations'));
const Users = lazy(() => import('./pages/setup/Users'));
const Properties = lazy(() => import('./pages/setup/Properties'));
const Account = lazy(() => import('./pages/setup/Account'));
const EmailLog = lazy(() => import('./pages/setup/EmailLog'));
const Settings = lazy(() => import('./pages/setup/Settings'));
const More = lazy(() => import('./pages/More'));
const Invite = lazy(() => import('./pages/Invite'));

/**
 * Old address → its PRD route, keeping the query (/receipts/new?po=… opens stock in with that PO). `to` may use the
 * route's params.
 */
function Redirect({ to }: { to: string | ((params: Params) => string) }) {
  const params = useParams();
  const { search } = useLocation();
  const target = new URL(typeof to === 'function' ? to(params) : to, 'http://app');
  new URLSearchParams(search).forEach((value, key) => { if (!target.searchParams.has(key)) target.searchParams.set(key, value); });
  return <Navigate to={target.pathname + target.search} replace />;
}

const moveKind = ({ kind }: Params) => (kind === 'transfer' ? '/stock/move?mode=transfer' : `/stock/move?mode=out&type=${kind}`);

export default function App() {
  const { me, loading, expired } = useSession();
  const { pathname } = useLocation();
  // Invite links open without a session (the invited person has no password yet).
  if (pathname.startsWith('/invite/')) {
    return <Suspense fallback={<Loading />}><Routes><Route path="/invite/:token" element={<Invite />} /></Routes></Suspense>;
  }
  if (loading) return <Loading />;
  if (!me) return <Login />;
  return (
    <Suspense fallback={<Loading />}>
      {expired && <SessionExpired email={me.user.email} />}
      <Routes>
        <Route element={<Shell />}>
          {/* PRD route table: Home, Tracking, Purchasing, Reporting, Settings (gear), More (phone). */}
          <Route index element={<Dashboard />} />
          <Route path="stock/move" element={<StockMove />} />
          <Route path="stock" element={<Inventory />} />
          <Route path="movements" element={<Movements />} />
          <Route path="items" element={<Items />} />
          <Route path="items/import" element={<ItemImport />} />
          <Route path="items/:id" element={<ItemProfile />} />
          <Route path="reorder" element={<Reorder />} />
          <Route path="purchase-requests" element={<PurchaseRequests />} />
          <Route path="purchase-requests/new" element={<PurchaseRequestForm />} />
          <Route path="purchase-requests/:id/edit" element={<PurchaseRequestForm />} />
          <Route path="purchase-requests/:id" element={<PurchaseRequestDetail />} />
          <Route path="purchase-orders" element={<PurchaseOrders />} />
          <Route path="purchase-orders/:id" element={<PurchaseOrderDetail />} />
          <Route path="suppliers" element={<Suppliers />} />
          <Route path="suppliers/:id" element={<SupplierProfile />} />
          <Route path="reports" element={<Reports />} />
          <Route path="periods" element={<Periods />} />
          <Route path="settings" element={<Settings />} />
          <Route path="settings/par" element={<ParLevels />} />
          <Route path="settings/kits" element={<Kits />} />
          <Route path="settings/locations" element={<Locations />} />
          <Route path="settings/users" element={<Users />} />
          <Route path="settings/property" element={<PropertySettings />} />
          <Route path="more" element={<More />} />
          <Route path="account" element={<Account />} />
          <Route path="properties" element={<Properties />} />
          <Route path="email-log" element={<EmailLog />} />
          {/* Sources linked from Movements (a receipt, a count); not in the menus. */}
          <Route path="receipts/:id" element={<ReceiptDetail />} />
          <Route path="counts" element={<Counts />} />
          <Route path="counts/:id" element={<CountDetail />} />

          {/* Old addresses. */}
          <Route path="receipts/new" element={<Redirect to="/stock/move?mode=in" />} />
          <Route path="receipts" element={<Redirect to="/movements?kind=in" />} />
          <Route path="movements/new/:kind" element={<Redirect to={moveKind} />} />
          <Route path="counts/new" element={<Redirect to="/stock/move?mode=count" />} />
          <Route path="locations" element={<Redirect to="/settings/locations" />} />
          <Route path="users" element={<Redirect to="/settings/users" />} />
          <Route path="audit" element={<Redirect to="/reports?tab=audit" />} />
          {/* Purchase orders are only made from purchase requests (PRD flow 9). */}
          <Route path="purchase-orders/new" element={<Redirect to="/purchase-requests" />} />
          <Route path="purchase-orders/:id/edit" element={<Redirect to={({ id }) => `/purchase-orders/${id}`} />} />
          {/* Requisitions were retired with PRD v2 (stock movement replaces them). */}
          <Route path="requisitions/*" element={<Redirect to="/movements" />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
